import React from 'react';
import { css } from '@emotion/react';
import { calcMilestone } from '@onward-delivery/core';
import { OnwardCard } from './Card';
import { skeletonStyles } from './blocks';
import LinkedOrderLink from './LinkedOrderLink';
import { colors } from '@/styles';
import { openItemExceptionTags } from '../SplitOrderModal/utils';

const HEADING = 'This order has been split into the following orders for delivery. Make changes on these orders.';
const ERROR_TEXT = "Couldn't load the child orders.";
const EMPTY_TEXT = 'No child orders found.';

/** Light amber behind an item-exception pill, from the split parent design. */
const EXCEPTION_PILL_BACKGROUND = '#FCE8B2';

const headingStyles = css`
    margin: 0 0 16px;
    font-size: 16px;
    font-weight: 700;
    color: ${colors.greys[3]};
`;

const rowStyles = css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 12px 0;
    font-weight: 700;
    color: ${colors.greys[3]};
`;

const linkStyles = css`
    color: ${colors.greens.primary};
    font-weight: 700;
    text-decoration: underline;
    &:hover {
        color: ${colors.greens.primary};
    }
`;

const pillStyles = css`
    display: inline-block;
    padding: 2px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    color: ${colors.greys[3]};
    background: ${colors.greys.border};
`;

const exceptionPillStyles = css`
    background: ${EXCEPTION_PILL_BACKGROUND};
`;

/**
 * The split parent's children panel: a numbered row per child with a bold green link, a light grey pill with its
 * milestone (the text My Orders shows), and a light amber pill for each open item-exception type. Replaces the checklist card on a split parent.
 *
 * @param {{ childOrders: object[], loading?: boolean, error?: Error }} props `childOrders` are in `split_seq` order,
 *   each with the order fields `calcMilestone` reads and `itemsByOrderId`.
 */
export const SplitChildrenCard = ({ childOrders = [], loading = false, error }) => {
    let body;
    if (loading) {
        body = (
            <div data-testid="split-children-loading">
                <div css={[skeletonStyles, { width: '60%' }]} />
                <div css={[skeletonStyles, { width: '50%' }]} />
            </div>
        );
    } else if (error) {
        body = <div role="alert">{ERROR_TEXT}</div>;
    } else if (childOrders.length === 0) {
        body = <div>{EMPTY_TEXT}</div>;
    } else {
        body = (
            <ol
                css={css`
                    list-style: none;
                    padding: 0;
                    margin: 0;
                `}
            >
                {childOrders.map((child, index) => {
                    const milestone = calcMilestone(child) || '-';
                    return (
                        <li key={child.order_id} css={rowStyles}>
                            <span>{index + 1}.</span>
                            <LinkedOrderLink orderId={child.order_id} css={linkStyles}>
                                {child.order_number}
                            </LinkedOrderLink>
                            <span css={pillStyles}>{milestone}</span>
                            {openItemExceptionTags(child.itemsByOrderId).map((tag) => (
                                <span key={tag.key} css={[pillStyles, exceptionPillStyles]}>
                                    {tag.label}
                                </span>
                            ))}
                        </li>
                    );
                })}
            </ol>
        );
    }

    return (
        <OnwardCard>
            <h5 css={headingStyles}>{HEADING}</h5>
            {body}
        </OnwardCard>
    );
};

export default SplitChildrenCard;
